import { ReactNode, createContext, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { LS_KEY_COOKIE_ACCEPTED_IDS } from 'consts';
import { getMainConfig } from 'utils';

import services from '../../services';

import CookieBanner from './CookieBanner';

type CookieBannerContextValue = {
  enabled: boolean;
  accepted: boolean;
  acceptedIds: string[];
  items: {
    id: string;
    title: string;
    description: string;
    checked: boolean;
  }[];
  mode: 'init' | 'update';
  open: boolean;
  openSettings: () => void;
  onClose: (reason: 'save' | 'cancel', checkedIds: string[]) => void;
};

export const CookieBannerContext = createContext<CookieBannerContextValue | null>(null);

type CookieBannerProviderProps = {
  children: ReactNode;
};

const fetchAcceptedIdsFromStorage = () => {
  return JSON.parse(services.appFrame.userStorage.getItem(LS_KEY_COOKIE_ACCEPTED_IDS) || '[]');
};

// inline styles are needed to avoid using `!important` as we have z-index conflict with the base component
const cookieBannerStyles = {
  zIndex: 2000,
};

const CookieBannerProvider = ({ children }: CookieBannerProviderProps) => {
  const configItems = useMemo(() => getMainConfig('cookie') || [], []);
  const cookiePolicyUrl = getMainConfig('cookiePolicyUrl');
  const enabled = configItems.length > 0;

  const { t } = useTranslation();
  const [mode, setMode] = useState<'init' | 'update'>('init');
  const [acceptedIds, setAcceptedIds] = useState(fetchAcceptedIdsFromStorage);
  const accepted = acceptedIds.length > 0;
  const [open, setOpen] = useState(!accepted);
  useEffect(() => {
    if (accepted && mode === 'init') {
      setOpen(false);
    }
  }, [mode, acceptedIds, accepted]);

  useEffect(() => {
    const onStorageChanged = (storageEvent) => {
      if (storageEvent.key !== LS_KEY_COOKIE_ACCEPTED_IDS) {
        return;
      }
      setAcceptedIds(fetchAcceptedIdsFromStorage());
    };
    window.addEventListener('storage', onStorageChanged);

    return () => {
      window.removeEventListener('storage', onStorageChanged);
    };
  }, []);

  const onClose = useCallback((reason, checkedIds) => {
    if (reason === 'save') {
      services.appFrame.userStorage.setItem(LS_KEY_COOKIE_ACCEPTED_IDS, JSON.stringify(checkedIds));
      setAcceptedIds(checkedIds);
    }

    setOpen(false);
  }, []);

  const items = useMemo(() => {
    return configItems.map((configItem) => ({
      ...configItem,
      title: t(`cookie.options.${configItem.id}.title`),
      description: t(`cookie.options.${configItem.id}.description`),
      checked: acceptedIds.includes(configItem.id) || configItem.checked,
    }));
  }, [acceptedIds, configItems, t]);

  const openSettings = useCallback(() => {
    if (open) {
      return;
    }
    setMode('update');
    setOpen(true);
  }, [open]);

  const contextValue = useMemo(() => {
    return {
      enabled,
      accepted,
      acceptedIds,
      items,
      mode,
      open,
      openSettings,
      onClose,
    };
  }, [enabled, accepted, acceptedIds, items, mode, open, openSettings, onClose]);

  return (
    <CookieBannerContext.Provider value={contextValue}>
      {children}
      <CookieBanner
        style={cookieBannerStyles}
        enabled={enabled}
        items={items}
        mode={mode}
        open={open}
        onClose={onClose}
        cookiePolicyUrl={cookiePolicyUrl}
      />
    </CookieBannerContext.Provider>
  );
};

export default CookieBannerProvider;
